import { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createQueryKeys } from '@lukemorales/query-key-factory';

import { CrazyAIQueryContext } from './CrazyAI';
import { queryError, queryResult } from './query';
import { useMainMachine } from '../utils/useMainMachine';

const queryKeys = createQueryKeys('crazyAI', {
  crazyai: ({ type, params, extraQueryKeyParams }) => [{ type, params, extraQueryKeyParams }],
});

export function useCrazyAI({ type, params, context, extraQueryKeyParams, enabled = true }) {
  const queryClient = useQueryClient({ context: CrazyAIQueryContext });
  const queryKey = useMemo(
    () => queryKeys.crazyai({ type, params, extraQueryKeyParams }).queryKey,
    [type, params, extraQueryKeyParams],
  );
  const { useSelector } = useMainMachine();
  const ai = useSelector(({ context }) => context.ai);
  const [error, setError] = useState(null);
  const [retry, setRetry] = useState(0);

  const refetch = useCallback(() => {
    queryClient.removeQueries({ queryKey });
    setRetry((x) => x + 1);
  }, [queryKey, queryClient]);

  useEffect(() => {
    let canceled = false;

    async function run() {
      if (enabled && ai && !queryClient.getQueryData(queryKey)) {
        setError(null);
        try {
          await ai.queryAsk(type, params, context, {
            onPayload: (payload) => {
              if (canceled) return;
              if (payload.status === 'error') {
                setError(queryError(payload));
                return;
              }
              if (payload.type !== 'message') return;

              queryClient.setQueryData(queryKey, (oldData) => queryResult(payload, oldData));
            },
          });
        } catch (e) {
          if (!canceled) setError(e);
        }
      }
    }
    run();

    return () => {
      canceled = true;
      if (!queryClient.getQueryData(queryKey)?.ready) {
        queryClient.removeQueries({ queryKey });
      }
    };
  }, [ai, queryKey, type, params, context, retry, queryClient, enabled]);

  const result = useQuery({ queryKey, context: CrazyAIQueryContext });

  const id = result.data?.queryId;

  const sendFeedback = useCallback(
    async (feedback) => {
      if (!id || !ai) return { error: 'No connection or no query.' };
      try {
        await ai.queryFeedback(id, feedback);
        const oldData = queryClient.getQueryData(queryKey);
        queryClient.setQueryData(queryKey, { ...oldData, feedback });
        return { data: 'ok' };
      } catch (error) {
        return { error };
      }
    },
    [id, ai, queryKey, queryClient],
  );

  if (error) return { error, refetch };

  return { ...result, refetch, sendFeedback };
}
